import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Cpu, RefreshCw } from 'lucide-react';
import { AI_PROVIDERS, listModels, localAIEnabled, modelProvider } from '../../services/ollama';
import { useAuth } from '../../contexts/AuthContext';
import AIConnectionDialog from './AIConnectionDialog';

export default function ModelPicker({ value, onChange, preferredModel = '', disabled = false }) {
  const { user } = useAuth();
  const storageKey = `spcc:ollama-model:${user?.id}`;
  const savedModel = () => localStorage.getItem(storageKey) || '';
  const [provider, setProvider] = useState(() => {
    const chosen = value || savedModel();
    return chosen && (localAIEnabled || modelProvider(chosen) !== 'ollama') ? modelProvider(chosen) : localAIEnabled ? 'ollama' : 'openai';
  });
  const [models, setModels] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [showConnect, setShowConnect] = useState(false);
  const requestState = useRef({ id: 0 }).current;
  const refresh = useCallback(async () => {
    const id = ++requestState.id;
    setLoading(true); setError(''); setModels([]);
    try {
      const response = await listModels(provider);
      if (id === requestState.id) setModels(response.models);
    } catch (e) { if (id === requestState.id) setError(e.message); }
    finally { if (id === requestState.id) setLoading(false); }
  }, [provider, requestState]);
  useEffect(() => {
    refresh();
    window.addEventListener('spcc:ai-connections-changed', refresh);
    return () => { requestState.id++; window.removeEventListener('spcc:ai-connections-changed', refresh); };
  }, [refresh, user?.id, requestState]);
  // Layout effect: the saved model is applied before paint, so the folded line
  // appears directly instead of the full controls flashing first.
  useLayoutEffect(() => {
    if (loading) return;
    const saved = localStorage.getItem(storageKey);
    const preferred = models.some(m => m.name === saved) ? saved : provider === 'ollama' ? preferredModel : '';
    if (value && !models.some(m => m.name === value)) onChange('');
    else if (!value && preferred && models.some(m => m.name === preferred)) onChange(preferred);
  }, [models, value, onChange, loading, preferredModel, storageKey, provider]);
  // Folded to one line once a model is chosen; the controls open on request or when there is a problem.
  const [expanded, setExpanded] = useState(false);
  const folded = !expanded && !error && (loading || !!value);
  const chosen = models.find(m => m.name === value);
  return <>
    {folded && <div className="v2-model-line" role="status"><Cpu aria-hidden="true"/>
      {loading && !value ? <span>Loading AI models…</span> : <>
        <span>AI model</span><strong>{chosen?.label || value}</strong><span>· {AI_PROVIDERS[provider]}</span>
        <button type="button" className="v2-link-button" disabled={disabled} onClick={() => setExpanded(true)}>Change</button>
      </>}
    </div>}
    <div className="v2-model-picker" hidden={folded}>
      <label className="v2-field"><span>AI provider</span><select value={provider} disabled={disabled} onChange={e => { setProvider(e.target.value); onChange(''); }}>
        {Object.entries(AI_PROVIDERS).map(([key,label]) => <option key={key} value={key}>{label}</option>)}
      </select></label>
      <label className="v2-field"><span>{provider === 'ollama' ? 'Local Ollama model' : 'AI model'}</span><select value={value} disabled={disabled || loading || !models.length} onChange={e => { onChange(e.target.value); localStorage.setItem(storageKey, e.target.value); }}>
        <option value="">{loading ? 'Loading models…' : 'Choose a model'}</option>
        {models.map(model => <option value={model.name} key={model.name}>{model.label || model.name}{model.parameters ? ` · ${model.parameters}` : ''}</option>)}
      </select></label>
      <button type="button" className="v2-button secondary" disabled={disabled || loading} onClick={refresh} aria-label="Refresh models"><RefreshCw size={16}/></button>
      {provider !== 'ollama' && <button type="button" className="v2-button secondary" disabled={disabled} onClick={() => setShowConnect(true)}>Manage AI connection</button>}
      {expanded && value && <button type="button" className="v2-button secondary" onClick={() => setExpanded(false)}>Done</button>}
      {error && <p role="alert" className="v2-error">{error}</p>}
      {!loading && !error && !models.length && <p>{provider === 'ollama' ? 'No models installed. Install a chat model in Ollama, then refresh.' : 'No text models available for this connection.'}</p>}
      {provider !== 'ollama' && <p className="v2-muted">Selected context is sent to {AI_PROVIDERS[provider]} using your connected account. Choose a model with structured output support.</p>}
    </div>
    <AIConnectionDialog key={user?.id} open={showConnect} onClose={() => setShowConnect(false)} initialProvider={provider}/>
  </>;
}
